'use client';
import React, { useEffect, useRef, useState } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { useChatbotVisibility } from '@/hooks/useChatbotVisibility';
import useFeature from '@/hooks/useFeature';

const Ada = () => {
  const enableAdaGreeting = Boolean(
    useFeature('retention-enable-ada-greeting'),
  );
  const adaEnvironment = useFeature('sub-squad-ada-environment');

  // EU/UK config
  const region = process.env.NEXT_PUBLIC_REGION;
  const isEU = region !== 'US';
  const cluster = 'eu';

  const { isDisabled } = useChatbotVisibility();
  const adaGreetingId = process.env.NEXT_PUBLIC_ADA_GREETING_ID || '';

  // State for localStorage values
  const [loveveryUserJwt, setLoveveryUserJwt] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string>('');
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  // Refs to hold latest values for onLoad callback
  const loveveryUserJwtRef = useRef(loveveryUserJwt);
  const firstNameRef = useRef(firstName);
  const isDrawerOpenRef = useRef(isDrawerOpen);

  // Keep refs in sync with state
  useEffect(() => {
    loveveryUserJwtRef.current = loveveryUserJwt;
    firstNameRef.current = firstName;
    isDrawerOpenRef.current = isDrawerOpen;
  }, [loveveryUserJwt, firstName, isDrawerOpen]);

  // Load localStorage values on client side
  useEffect(() => {
    const jwt = localStorage.getItem('le-user-jwt');
    const user = localStorage.getItem('user');
    const drawerOpen =
      localStorage.getItem('ada-embed_is-drawer-open') === 'true';

    setLoveveryUserJwt(jwt);

    if (user) {
      try {
        setFirstName(JSON.parse(user)?.attributes?.name || '');
      } catch (e) {
        setFirstName('');
      }
    } else {
      setFirstName('');
    }

    setIsDrawerOpen(drawerOpen);
  }, []);

  const initializeAdaBot = async () => {
    if (!window.adaEmbed) {
      console.error('Ada script is not available.');
      return;
    }

    // Read from refs to get latest values
    const currentFirstName = firstNameRef.current;
    const currentJwt = loveveryUserJwtRef.current;
    const currentDrawerOpen = isDrawerOpenRef.current;

    const adaSettings = {
      handle: adaEnvironment,
      ...(isEU && { cluster }),
      metaFields: {
        location_origin: window.location.origin,
        ...(currentFirstName && { first_name: currentFirstName }),
      },
      sensitiveMetaFields: {
        ...(currentJwt && { lovevery_user_jwt: currentJwt }),
      },
    };

    window.adaEmbed.start(adaSettings);

    if (currentFirstName) {
      window.adaEmbed.setMetaFields({
        first_name: currentFirstName,
      });
    }

    if (currentDrawerOpen) {
      await window.adaEmbed.toggle();
    }

    if (currentJwt) {
      window.adaEmbed.setSensitiveMetaFields({
        lovevery_user_jwt: currentJwt,
      });
    }
  };

  useEffect(() => {
    if (loveveryUserJwt && isDrawerOpen && enableAdaGreeting) {
      window.adaEmbed?.triggerAnswer(adaGreetingId);
    }
  }, [loveveryUserJwt, isDrawerOpen, enableAdaGreeting, adaGreetingId]);

  // Helper function to show or hide Ada widget
  const setAdaWidgetVisibility = (visible: boolean) => {
    const adaButton = document.getElementById('ada-button-frame');
    const adaDrawer = document.getElementById('ada-close-button');
    if (adaButton) {
      adaButton.style.display = visible ? 'block' : 'none';
    }
    if (adaDrawer) {
      adaDrawer.style.display = visible ? 'block' : 'none';
    }
  };

  // Cleanup Ada widget when component unmounts
  useEffect(() => {
    return () => {
      setAdaWidgetVisibility(false);
    };
  }, []);

  // Hide/show Ada widget based on isDisabled state
  useEffect(() => {
    setAdaWidgetVisibility(!isDisabled);
  }, [isDisabled]);

  if (isDisabled || !enableAdaGreeting) {
    return null;
  }

  return (
    <>
      <Head>
        <style>
          {`
          #ada-button-frame {
            bottom: 1rem !important;
          }
        `}
        </style>
      </Head>
      <Script
        async
        data-testid="ada"
        id="__ada"
        data-lazy
        data-handle={adaEnvironment}
        data-cluster={isEU ? cluster : undefined}
        src="https://static.ada.support/embed2.js"
        onLoad={initializeAdaBot}
      />
    </>
  );
};

export default React.memo(Ada);
